import { c as _c } from "react/compiler-runtime";
import { Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { ChatLoadingState } from '@/modules/common/Chat/components/ChatStatusRow';
import { COLORS } from '@/styles/tokens/colors';
import { ViewConfiguration } from '../BalanceSheetTable/BalanceSheetTableContext';
import { AppliedSuggestion, BalanceSheetSuggestionPreset, getAvailableSuggestions } from './BalanceSheetConfigPanel.suggestions';
import { BalanceSheetConfigSuggestionBubble } from './BalanceSheetConfigSuggestion';
interface BalanceSheetConfigChatProps {
  localConfig: ViewConfiguration;
  configHistoryLength: number;
  appliedSuggestions: AppliedSuggestion[];
  isSuggestionThinking: boolean;
  onApplyPreset: (preset: BalanceSheetSuggestionPreset) => void;
  onUndo: () => void;
}

/**
 * Chat-style suggestion UI for the balance sheet config panel.
 * Shows a history of applied suggestions and available presets
 * the user can click to modify the balance sheet configuration.
 */
export function BalanceSheetConfigChat(t0) {
  const $ = _c(37);
  const {
    localConfig,
    configHistoryLength,
    appliedSuggestions,
    isSuggestionThinking,
    onApplyPreset,
    onUndo
  } = t0;
  const bottomRef = useRef(null);
  let t1;
  if ($[0] !== localConfig) {
    t1 = getAvailableSuggestions(localConfig);
    $[0] = localConfig;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const suggestions = t1;
  const showUndoSuggestion = configHistoryLength > 0;
  const hasAppliedSuggestions = appliedSuggestions.length > 0;
  let t2;
  if ($[2] !== hasAppliedSuggestions) {
    t2 = () => {
      if (hasAppliedSuggestions) {
        bottomRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end"
        });
      }
    };
    $[2] = hasAppliedSuggestions;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== appliedSuggestions.length || $[5] !== hasAppliedSuggestions) {
    t3 = [appliedSuggestions.length, hasAppliedSuggestions];
    $[4] = appliedSuggestions.length;
    $[5] = hasAppliedSuggestions;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== appliedSuggestions || $[8] !== hasAppliedSuggestions) {
    t4 = hasAppliedSuggestions && <Stack gap={1.5}>{appliedSuggestions.map(_temp)}</Stack>;
    $[7] = appliedSuggestions;
    $[8] = hasAppliedSuggestions;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Typography variant="title-s">Configuration suggestions</Typography>;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Stack gap={0.5}>{t5}<Typography variant="body-xs" color={COLORS.TEXT.PRIMARY}>Make additional updates to the balance sheet layout with the below configuration options.<br />The value of any hidden entities will not be included in display metrics at top of page.</Typography></Stack>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isSuggestionThinking || $[13] !== onApplyPreset || $[14] !== suggestions) {
    let t8;
    if ($[16] !== isSuggestionThinking || $[17] !== onApplyPreset) {
      t8 = preset => <BalanceSheetConfigSuggestionBubble key={preset.id} preset={preset} onApply={onApplyPreset} disabled={isSuggestionThinking} />;
      $[16] = isSuggestionThinking;
      $[17] = onApplyPreset;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    t7 = suggestions.map(t8);
    $[12] = isSuggestionThinking;
    $[13] = onApplyPreset;
    $[14] = suggestions;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[19] !== isSuggestionThinking || $[20] !== onUndo || $[21] !== showUndoSuggestion) {
    t8 = showUndoSuggestion && <BalanceSheetConfigSuggestionBubble preset={{
      id: "undo",
      label: "Undo previous change",
      config: {} as BalanceSheetSuggestionPreset['config'],
      responseMessage: ""
    }} onApply={onUndo} disabled={isSuggestionThinking} />;
    $[19] = isSuggestionThinking;
    $[20] = onUndo;
    $[21] = showUndoSuggestion;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t7 || $[24] !== t8) {
    t9 = <Stack gap={1}>{t7}{t8}</Stack>;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== isSuggestionThinking) {
    t10 = isSuggestionThinking && <ChatLoadingState />;
    $[26] = isSuggestionThinking;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const t11 = isSuggestionThinking ? 0 : "32px";
  let t12;
  if ($[28] !== t11) {
    t12 = <div style={{
      height: t11
    }} ref={bottomRef} />;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t10 || $[31] !== t12 || $[32] !== t9) {
    t13 = <Stack gap={2}>{t6}{t9}{t10}{t12}</Stack>;
    $[30] = t10;
    $[31] = t12;
    $[32] = t9;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t13 || $[35] !== t4) {
    t14 = <>{t4}{t13}</>;
    $[34] = t13;
    $[35] = t4;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
}
function _temp(applied, index) {
  return <Stack key={index} gap={1.5}><BalanceSheetConfigSuggestionBubble preset={applied.preset} completed={true} /><Typography variant="body-xs" color={COLORS.TEXT.PRIMARY}>{applied.preset.responseMessage}</Typography></Stack>;
}